import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Nav } from '../nav/nav';
import { TopHeader } from '../top-header/top-header';
import { RenterListComponent } from './renter-list/renter-list.component';
import { AssignFlatComponent } from './assign-flat/assign-flat.component';
import { AddEditRenterComponent, RenterSaveEvent } from './renter-list/add-edit-renter/add-edit-renter.component';
import { FilterField, FilterModalComponent } from '../core/ui/filter-modal/filter-modal.component';
import { TranslatePipe } from '../core/pipes/translate.pipe';

type RenterTab = 'Renter' | 'AssignFlat';

@Component({
  selector: 'app-renter',
  standalone: true,
  imports: [
    CommonModule,
    MatSnackBarModule,
    Nav,
    TopHeader,
    RenterListComponent,
    AssignFlatComponent,
    AddEditRenterComponent,
    FilterModalComponent,
    TranslatePipe
  ],
  templateUrl: './renter.html',
  styleUrls: ['./renter.css', './../../CSSFiles/Tabs.css', './../../CSSFiles/BTN_Style.css' ]
})
export class RenterComponent {
  @ViewChild(RenterListComponent) renterList?: RenterListComponent;
  @ViewChild(AssignFlatComponent) assignFlat?: AssignFlatComponent;
  activeTab: RenterTab = 'Renter';
  showNewRenter = false;
  showPostCreateAssignPrompt = false;
  showFilter = false;
  renterFilterValues: Record<string, any> = {};
  assignFilterValues: Record<string, any> = {};
  renterFilterActive = false;
  assignFilterActive = false;
  pendingCreatedRenterForAssign: { id: number | null; renterName: string } | null = null;

  constructor(private readonly snackBar: MatSnackBar) {}

  renterFilterFields: FilterField[] = [
    this.createHouseFilterField('flat.house.id'),
    this.createFloorFilterField('flat.floorName', 'flat.house.id'),
    this.createFlatFilterField('flat.id', 'flat.house.id'),
    {
      key: 'renterName',
      label: 'filter.renterName',
      type: 'text',
      placeholder: 'placeholder.typeRenterName'
    },
    {
      key: 'renterMobile',
      label: 'filter.mobile',
      type: 'text',
      placeholder: 'placeholder.typeMobileNumber'
    },
    {
      key: 'renterEmail',
      label: 'filter.email',
      type: 'text',
      placeholder: 'placeholder.typeEmail'
    },
    {
      key: 'renterPhotoIdNo',
      label: 'filter.photoId',
      type: 'text',
      placeholder: 'placeholder.typePhotoId'
    },
    {
      key: '__showDeleted',
      label: 'common.data',
      type: 'switch',
      switchLabel: 'common.showDeletedData'
    }
  ];

  assignFilterFields: FilterField[] = [
    this.createHouseFilterField('flat.house.id'),
    this.createFloorFilterField('flat.floorName', 'flat.house.id'),
    this.createFlatFilterField('flat.id', 'flat.house.id'),
    {
      key: 'renter.renterName',
      label: 'filter.renterName',
      type: 'text',
      placeholder: 'placeholder.typeRenterName'
    },
    {
      key: 'renter.renterMobile',
      label: 'filter.renterMobile',
      type: 'text',
      placeholder: 'placeholder.typeRenterMobile'
    },
    {
      key: 'flat.flatName',
      label: 'filter.flatName',
      type: 'text',
      placeholder: 'placeholder.typeFlatName'
    },
    {
      key: '__showDeleted',
      label: 'common.data',
      type: 'switch',
      switchLabel: 'common.showDeletedData'
    }
  ];

  selectTab(tab: RenterTab): void {
    if (this.activeTab !== tab) {
      if (tab === 'Renter') {
        this.renterFilterValues = { __showDeleted: false };
        this.renterFilterActive = false;
        this.renterList?.setFilters({ status: 1 });
      } else {
        this.assignFilterValues = { __showDeleted: false };
        this.assignFilterActive = false;
        this.assignFlat?.setFilters({ status: 1 });
      }
    }
    this.activeTab = tab;
    this.showFilter = false;
  }

  openNewRenter(): void {
    if (this.renterList?.isCreateDisabled) {
      this.openLimitToast(this.renterList.showCreateLimitMessage());
      return;
    }
    this.showNewRenter = true;
  }

  closeNewRenter(): void {
    this.showNewRenter = false;
  }

  handleRenterSave(event?: RenterSaveEvent): void {
    this.showNewRenter = false;
    this.renterList?.fetchRenters();
    if (event?.mode === 'create') {
      this.pendingCreatedRenterForAssign = {
        id: event.refId ?? null,
        renterName: event.renterName || 'Renter'
      };
    }
    if (event?.mode === 'create' && Number.isFinite(Number(event?.refId))) {
      this.renterList?.openUploadAfterCreate(Number(event.refId));
    }
  }

  handleRenterPostCreateUploadFinished(): void {
    if (!this.pendingCreatedRenterForAssign) {
      return;
    }
    this.showPostCreateAssignPrompt = true;
  }

  toggleFilter(): void {
    this.showFilter = !this.showFilter;
  }

  isFilterActive(): boolean {
    return this.activeTab === 'Renter' ? this.renterFilterActive : this.assignFilterActive;
  }

  applyFilters(values: Record<string, any>, isActive = false): void {
    this.showFilter = false;
    if (this.activeTab === 'Renter') {
      const normalized = this.normalizeFilters(values);
      this.renterFilterValues = { ...values };
      this.renterFilterActive = isActive;
      this.renterList?.setFilters(normalized);
      return;
    }

    const normalized = this.normalizeFilters(values);
    this.assignFilterValues = { ...values };
    this.assignFilterActive = isActive;
    this.assignFlat?.setFilters(normalized);
  }

  resetFilters(): void {
    if (this.activeTab === 'Renter') {
      const defaultFilters = { status: 1 };
      this.renterFilterValues = { __showDeleted: false };
      this.renterFilterActive = false;
      this.renterList?.setFilters(defaultFilters);
      return;
    }

    const defaultFilters = { status: 1 };
    this.assignFilterValues = { __showDeleted: false };
    this.assignFilterActive = false;
    this.assignFlat?.setFilters(defaultFilters);
  }

  openCreateAssign(): void {
    this.openAssignFlatModal();
  }

  closePostCreateAssignPrompt(): void {
    this.showPostCreateAssignPrompt = false;
    this.pendingCreatedRenterForAssign = null;
  }

  openAssignCreateFromRenterPrompt(): void {
    this.activeTab = 'AssignFlat';
    this.showFilter = false;
    this.openAssignFlatModal(this.pendingCreatedRenterForAssign);
    this.showPostCreateAssignPrompt = false;
    this.pendingCreatedRenterForAssign = null;
  }

  private normalizeFilters(values: Record<string, any>): Record<string, any> {
    const showDeleted = !!values?.['__showDeleted'];
    const { __showDeleted, ...rest } = values || {};
    return {
      ...rest,
      status: showDeleted ? 0 : 1
    };
  }

  private openLimitToast(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['dashboard-toast-warning']
    });
  }

  private openAssignFlatModal(preselectedRenter?: { id: number | null; renterName: string } | null): void {
    setTimeout(() => {
      this.assignFlat?.openCreateModal(preselectedRenter ?? null);
    }, 0);
  }

  private createHouseFilterField(key: string): FilterField {
    return {
      key,
      label: 'filter.house',
      type: 'select',
      placeholder: 'placeholder.selectHouse',
      dropdownObj: {
        apiInfo: 'HOUSE_LIST',
        name: key,
        valueKey: 'id',
        display1: 'houseName',
        display2: 'houseNo',
        label: 'filter.house',
        placeholder: 'placeholder.selectHouse',
        isSearchable: true,
        status: 1
      }
    };
  }

  private createFloorFilterField(key: string, dependsOnKey: string): FilterField {
    return {
      key,
      label: 'filter.floor',
      type: 'select',
      placeholder: 'placeholder.selectFloor',
      dependsOnKey,
      dependsOnOptionsKey: 'floors',
      dropdownObj: {
        name: key,
        valueKey: 'value',
        display1: 'label',
        label: 'filter.floor',
        placeholder: 'placeholder.selectFloor',
        options: [],
        isSearchable: true,
        searchReadonly: true
      }
    };
  }

  private createFlatFilterField(key: string, dependsOnKey: string): FilterField {
    return {
      key,
      label: 'filter.flat',
      type: 'select',
      placeholder: 'placeholder.selectFlat',
      dependsOnKey,
      dependsOnFilterKey: 'house.id',
      dropdownObj: {
        apiInfo: 'FLAT_LIST',
        name: key,
        valueKey: 'id',
        display1: 'flatName',
        display2: 'floorName',
        label: 'filter.flat',
        placeholder: 'placeholder.selectFlat',
        isSearchable: true,
        status: 1
      }
    };
  }
}
